import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  signal,
} from "@angular/core";
import { MAT_DIALOG_DATA, MatDialogRef } from "@angular/material/dialog";
import { CommonModule, DecimalPipe } from "@angular/common";
import { MatIcon } from "@angular/material/icon";
import { MatTooltip } from "@angular/material/tooltip";
import { DialogHeaderExportComponent } from "../../../../shared/modules/dialog/dialog-header-export/dialog-header-export.component";
import { SharedModule } from "../../../../shared/shared.module";
import { RadioCardComponent } from "../../../../shared/components/radio-card/radio-card.component";
import { LotCardComponent } from "../../../../shared/components/lot-card/lot-card.component";
import { ChipsToggleComponent } from "../../../../shared/modules/chips-toggle/chips-toggle.component";
import { ArticlesInfosFacadeService } from "../../../articles/services/article-infos/article-infos-facade.service";
import { parseNumber } from "../../../../shared/utils/number-utils.service";
import {
  CaPeriodeBucket,
  CaPeriodeScale,
  periodKey,
  periodLabel,
} from "./ca-par-periode-tiers.component";
import { exportCaParPeriodeExcel } from "./ca-par-periode-excel.utils";

export interface ResolveArticleName {
  (articleId: string): string;
}

export interface CaParPeriodeDialogData {
  title: string;
  tiersDenomination: string;
  buckets: CaPeriodeBucket[];
  facturesLignes: any[];
  lotCostMap: Map<string, { mat: number; mo: number }>;
  scale: CaPeriodeScale;
  articleFilterId: string | null;
  articleFilterName: string | null;
  totalCA: number;
  totalCost: number;
  totalCostMat: number;
  totalCostMO: number;
  totalMargin: number;
  totalMarginPct: number;
}

interface LotRow {
  key: string;
  lotId: string | null;
  lotNumero: string;
  qty: number;
  qtyUG: number;
  prixUnitaire: number;
  ca: number;
  costMat: number;
  costMO: number;
  cost: number;
  margin: number;
  marginPct: number;
}

interface ArticleRow {
  key: string;
  articleId: string;
  articleName: string;
  ca: number;
  costMat: number;
  costMO: number;
  cost: number;
  margin: number;
  marginPct: number;
  lots: LotRow[];
}

interface PeriodRow {
  key: string;
  label: string;
  d: Date;
  ca: number;
  costMat: number;
  costMO: number;
  cost: number;
  margin: number;
  marginPct: number;
  articles: ArticleRow[];
}

@Component({
  selector: "vl-ca-par-periode-dialog",
  standalone: true,
  imports: [
    CommonModule,
    SharedModule,
    DialogHeaderExportComponent,
    DecimalPipe,
    MatIcon,
    MatTooltip,
    RadioCardComponent,
    LotCardComponent,
    ChipsToggleComponent,
  ],
  templateUrl: "./ca-par-periode-dialog.component.html",
  styleUrls: ["./ca-par-periode-dialog.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CaParPeriodeDialogComponent {
  data = inject<CaParPeriodeDialogData>(MAT_DIALOG_DATA);
  dialogRef = inject(MatDialogRef<CaParPeriodeDialogComponent>);
  private articleInfosFacade = inject(ArticlesInfosFacadeService);

  expanded = signal<Set<string>>(new Set());
  exporting = signal(false);

  // Granularité pilotée dans le popup
  scale = signal<CaPeriodeScale>(this.data.scale);
  scaleElements = [
    { name: "Jour", value: "DAY" as CaPeriodeScale },
    { name: "Semaine", value: "WEEK" as CaPeriodeScale },
    { name: "Mois", value: "MONTH" as CaPeriodeScale },
  ];
  scaleLabel: Record<CaPeriodeScale, string> = {
    DAY: "Jour",
    WEEK: "Semaine",
    MONTH: "Mois",
  };
  scaleLabelPlural: Record<CaPeriodeScale, string> = {
    DAY: "Jours",
    WEEK: "Semaines",
    MONTH: "Mois",
  };

  /**
   * Construit l'arbre : période → article → lot.
   * Recalculé à chaque changement de granularité (scale).
   */
  rows = computed<PeriodRow[]>(() => {
    const scale = this.scale();
    const costMap = this.data.lotCostMap;
    const articleFilter = this.data.articleFilterId;

    // Période → Article → Lot (accumulateurs)
    const periodMap = new Map<
      string,
      {
        d: Date;
        label: string;
        ca: number;
        costMat: number;
        costMO: number;
        articles: Map<
          string,
          {
            articleId: string;
            ca: number;
            costMat: number;
            costMO: number;
            lots: Map<
              string,
              {
                lotId: string | null;
                lotNumero: string;
                qty: number;
                qtyUG: number;
                ca: number;
                costMat: number;
                costMO: number;
                prixCumul: number;
                prixWeight: number;
              }
            >;
          }
        >;
      }
    >();

    for (const fl of this.data.facturesLignes) {
      const f = fl?.facture ?? fl?.f;
      const dRaw = f?.date ?? f?.dateString ?? fl?.createdAt;
      if (!dRaw) continue;
      const d = new Date(dRaw);
      if (isNaN(d.getTime())) continue;

      const articleId: string =
        fl?.commandeClientLigne?.articleId ??
        fl?.articleId ??
        fl?.article?.id ??
        "inconnu";
      if (articleFilter && String(articleId) !== String(articleFilter)) continue;

      const coefUG =
        parseNumber(fl?.commandeClientLigne?.coefficientUniteGestion ?? 1) || 1;
      const qtyUV = parseNumber(fl?.quantite ?? 0);
      const qtyUG = qtyUV * coefUG;
      const puVenteUV = parseNumber(fl?.prix ?? 0);
      const ca = qtyUV * puVenteUV;

      const lotId: string | null = fl?.lotId ?? fl?.lot?.id ?? null;
      const lotNumero: string = fl?.lot?.numero ?? "—";
      const lotCosts = lotId ? costMap.get(lotId) : null;
      const costMat = qtyUG * (lotCosts?.mat ?? 0);
      const costMO = qtyUG * (lotCosts?.mo ?? 0);

      const pk = periodKey(d, scale);
      if (!periodMap.has(pk)) {
        periodMap.set(pk, {
          d,
          label: periodLabel(d, scale),
          ca: 0,
          costMat: 0,
          costMO: 0,
          articles: new Map(),
        });
      }
      const per = periodMap.get(pk)!;
      per.ca += ca;
      per.costMat += costMat;
      per.costMO += costMO;

      if (!per.articles.has(articleId)) {
        per.articles.set(articleId, {
          articleId,
          ca: 0,
          costMat: 0,
          costMO: 0,
          lots: new Map(),
        });
      }
      const art = per.articles.get(articleId)!;
      art.ca += ca;
      art.costMat += costMat;
      art.costMO += costMO;

      const lotKey = lotId ?? `_nolot_${lotNumero}`;
      if (!art.lots.has(lotKey)) {
        art.lots.set(lotKey, {
          lotId,
          lotNumero,
          qty: 0,
          qtyUG: 0,
          ca: 0,
          costMat: 0,
          costMO: 0,
          prixCumul: 0,
          prixWeight: 0,
        });
      }
      const lot = art.lots.get(lotKey)!;
      lot.qty += qtyUV;
      lot.qtyUG += qtyUG;
      lot.ca += ca;
      lot.costMat += costMat;
      lot.costMO += costMO;
      lot.prixCumul += puVenteUV * qtyUV;
      lot.prixWeight += qtyUV;
    }

    // Matérialisation triée
    const result: PeriodRow[] = [];
    for (const [pk, per] of periodMap.entries()) {
      const articles: ArticleRow[] = [];
      for (const art of per.articles.values()) {
        const cost = art.costMat + art.costMO;
        const margin = art.ca - cost;
        const marginPct = art.ca > 0 ? margin / art.ca : 0;
        const lots: LotRow[] = [...art.lots.values()]
          .map((l) => {
            const cost = l.costMat + l.costMO;
            const margin = l.ca - cost;
            const marginPct = l.ca > 0 ? margin / l.ca : 0;
            return {
              key: `${pk}::${art.articleId}::${l.lotId ?? l.lotNumero}`,
              lotId: l.lotId,
              lotNumero: l.lotNumero,
              qty: l.qty,
              qtyUG: l.qtyUG,
              prixUnitaire:
                l.prixWeight > 0 ? l.prixCumul / l.prixWeight : 0,
              ca: l.ca,
              costMat: l.costMat,
              costMO: l.costMO,
              cost,
              margin,
              marginPct,
            };
          })
          .sort((a, b) => b.ca - a.ca);

        articles.push({
          key: `${pk}::${art.articleId}`,
          articleId: art.articleId,
          articleName:
            this.articleInfosFacade.getByArticleId(art.articleId)?.()
              ?.denomination ?? "Inconnu",
          ca: art.ca,
          costMat: art.costMat,
          costMO: art.costMO,
          cost,
          margin,
          marginPct,
          lots,
        });
      }
      articles.sort((a, b) => b.ca - a.ca);

      const cost = per.costMat + per.costMO;
      const margin = per.ca - cost;
      const marginPct = per.ca > 0 ? margin / per.ca : 0;
      result.push({
        key: pk,
        label: per.label,
        d: per.d,
        ca: per.ca,
        costMat: per.costMat,
        costMO: per.costMO,
        cost,
        margin,
        marginPct,
        articles,
      });
    }
    result.sort((a, b) => b.d.getTime() - a.d.getTime());
    return result;
  });

  /** Stats agrégées sur le scale courant : top période, CA moyen, Pareto 80 %. */
  stats = computed(() => {
    const rs = this.rows();
    if (!rs.length) {
      return {
        bestLabel: "—",
        bestPct: 0,
        count: 0,
        avgCA: 0,
        pareto80Count: 0,
      };
    }
    const sortedByPct = [...rs].sort((a, b) => b.marginPct - a.marginPct);

    // Pareto 80 % : nb de périodes qui cumulent 80 % du CA.
    const sortedByCa = [...rs].sort((a, b) => b.ca - a.ca);
    const totalCa = this.data.totalCA || 1;
    let cum = 0;
    let p80 = 0;
    for (const r of sortedByCa) {
      cum += r.ca;
      p80++;
      if (cum >= totalCa * 0.8) break;
    }

    return {
      bestLabel: sortedByPct[0].label,
      bestPct: sortedByPct[0].marginPct,
      count: rs.length,
      avgCA: totalCa / rs.length,
      pareto80Count: p80,
    };
  });

  toggle(k: string, event?: Event) {
    event?.stopPropagation();
    const cur = new Set(this.expanded());
    if (cur.has(k)) cur.delete(k);
    else cur.add(k);
    this.expanded.set(cur);
  }

  isOpen(k: string): boolean {
    return this.expanded().has(k);
  }

  pctClass(pct: number): string {
    const p = pct * 100;
    if (p < 5) return "on-error";
    if (p < 15) return "on-warning";
    if (p < 30) return "on-warning2";
    return "on-valid";
  }

  colorForPct(pct: number): string {
    const p = pct * 100;
    if (p < 0) return "#dc2626";
    if (p < 5) return "#ef4444";
    if (p < 15) return "#f97316";
    if (p < 30) return "#facc15";
    return "#10b981";
  }

  formatEuro(v: number): string {
    const n = Number(v ?? 0);
    if (!isFinite(n)) return "—";
    const abs = Math.abs(n);
    if (abs >= 1_000_000)
      return `${(n / 1_000_000).toLocaleString("fr-FR", { maximumFractionDigits: 2 })} M€`;
    if (abs >= 10_000)
      return `${(n / 1_000).toLocaleString("fr-FR", { maximumFractionDigits: 1 })} k€`;
    return `${n.toLocaleString("fr-FR", { maximumFractionDigits: 0 })} €`;
  }

  /** Format exact sans abréviation (pour les colonnes CA des lignes du tree). */
  formatEuroExact(v: number): string {
    const n = Number(v ?? 0);
    if (!isFinite(n)) return "—";
    return `${n.toLocaleString("fr-FR", { maximumFractionDigits: 0 })} €`;
  }

  /** Part d'un sous-ensemble dans le total CA (comme dans repartition-ca-dialog). */
  percentPart(ca: number): string {
    const total = this.data.totalCA || 1;
    return ((ca / total) * 100).toFixed(1);
  }

  async exportExcel() {
    this.exporting.set(true);
    try {
      await exportCaParPeriodeExcel(
        { ...this.data, scale: this.scale() },
        (articleId: string) =>
          this.articleInfosFacade.getByArticleId(articleId)?.()?.denomination ??
          "Inconnu",
      );
    } finally {
      this.exporting.set(false);
    }
  }
}
